import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ESTIMATED_KIND_KEY, type ActivityType, type KindHours } from "@torre/work-hours";
import type { ThemeClasses } from "../../../types/components";
import { kindLabel } from "../lib/kindLabel";
import { byPerson, countedLines, groupLines, hoursOf, sumCost, type Ledger } from "../lib/ledger";
import { CountedLinesTable, ExpandRow, HoursAndMoney, PeopleSubtotals } from "./LedgerParts";

// ---------------------------------------------------------------------------
// «Por tipo de actividad» (#1342 / #1344): lo que cuenta en el rango según el
// tipo de cada entrada (catálogo de actividades): desarrollo, reunión,
// prospección, contenido… El último renglón son los días sin registro,
// estimados por commits: no tienen tipo.
//
// Cada tipo se abre: el subtotal por colaborador y, debajo, las entradas que lo
// suman (quién, cuándo trabajó, cuándo lo registró, cuánto costó). Los importes
// solo llegan a los dueños.
// ---------------------------------------------------------------------------

interface Props {
  byKind: KindHours[];
  catalog: readonly ActivityType[] | null;
  /** Las líneas detrás de cada tipo; `null` mientras cargan (los renglones no se abren). */
  ledger: Ledger | null;
  tc: ThemeClasses;
  /** Con una sola persona en el resultado, la columna de personas sobra. */
  showPeople: boolean;
  /** Dentro de una pestaña que ya lleva el título. */
  hideTitle?: boolean;
}

export default function KindHoursTable({ byKind, catalog, ledger, tc, showPeople, hideTitle = false }: Props) {
  const { t, i18n } = useTranslation("common");
  const [open, setOpen] = useState<string | null>(null);
  const groups = useMemo(() => new Map((ledger ? groupLines(countedLines(ledger), (l) => l.kind) : []).map((g) => [g.key, g])), [ledger]);
  const costs = Boolean(ledger?.costs);
  if (!byKind.length) return null;
  return (
    <div data-testid="kind-hours">
      {!hideTitle && <p className={`mb-1 text-sm font-semibold ${tc.text}`}>{t("workHours.byKind.title")}</p>}
      <p className={`mb-2 text-xs ${tc.textSecondary}`}>{t("workHours.byKind.subtitle")}</p>
      {byKind.map((k) => {
        const estimated = k.kind === ESTIMATED_KIND_KEY;
        const id = k.kind || "estimated";
        const group = groups.get(k.kind);
        return (
          <ExpandRow
            key={id}
            tc={tc}
            testId={`kind-hours-${id}`}
            open={open === id}
            onToggle={() => setOpen(open === id ? null : id)}
            disabled={!group}
            label={
              <>
                {estimated ? t("workHours.byKind.estimated") : kindLabel(k.kind, catalog, t, i18n?.language)}
                <span className={`block text-[11px] ${tc.textSecondary}`}>
                  {[
                    estimated ? null : t("workHours.byKind.entriesCount", { count: k.entries }),
                    showPeople ? t("workHours.byKind.peopleCount", { count: k.people }) : null,
                    k.crmMinutes > 0 ? t("workHours.byKind.fromCrm", { hours: hoursOf(k.crmMinutes) }) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </>
            }
            right={<HoursAndMoney minutes={k.minutes} money={group ? group.money : sumCost([])} costs={costs} tc={tc} />}
          >
            {group && (
              <>
                <PeopleSubtotals people={byPerson(group.lines)} costs={costs} tc={tc} />
                <CountedLinesTable lines={group.lines} costs={costs} catalog={catalog} tc={tc} hideKind testId={`kind-lines-${id}`} />
              </>
            )}
          </ExpandRow>
        );
      })}
    </div>
  );
}
